if (!customElements.get('predictive-search')) {
  /**
   * PredictiveSearch
   * @description Uses the Shopify predictive search api for rendering search results.
   * @link https://shopify.dev/api/ajax/reference/predictive-search
   */
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();

      // Options
      this.options = {
        hiddenClass: 'hidden',
      };

      // Get options from element data and combine with this.options
      if (this?.dataset?.options) {
        const dataOptions = JSON.parse(this.dataset.options);
        this.options = {
          ...this.options,
          ...dataOptions,
        };
      }

      this.init();
    }

    /**
     * init
     * @description Select elements and add events.
     */
    init() {
      this.input = this.querySelector('[data-predicative-search="input"]');
      this.reset = this.querySelector('[data-predicative-search="reset"]');
      this.predictiveSearchResults = this.querySelector('[data-predicative-search="results"]');
      this.predictiveSearchResources =
        this.predictiveSearchResults.getAttribute('data-resources-types') || 'product';
      this.predictiveSearchMaxSize =
        this.predictiveSearchResults.getAttribute('data-max-results') || 5;
      this.popularTerms = this.querySelectorAll('[data-predicative-search="popular-terms"]');
      this.backdrop = this.querySelector('[data-predictive-search-backdrop]');
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 100).bind(this)
      );

      this.handleInputState();

      // Handle reset click
      this.reset.addEventListener('click', () => {
        this.clearInput();
        this.handleInputState();
      });

      // Handle typing
      this.input.addEventListener('keyup', (e) => {
        if (e.key === 'Escape') {
          this.close();
          this.hidePopularTerms();
        }
      });

      this.input.addEventListener('keydown', () => {
        this.handleInputState();
      });

      this.input.addEventListener('focus', () => {
        this.open();
        this.showPopularTerms();
      });
    }

    /**
     * onChange
     * @description Checks the input value and will call the fetch data function
     * @returns {Void}
     */
    onChange() {
      const searchTerm = this.input.value.trim();
      if (!searchTerm.length) {
        this.close();
        return;
      }
      this.getSearchResults(searchTerm);
    }

    /**
     * getSearchResults
     * @description Fetch data from the predictive search api.
     * @param {String} searchTerm
     */
    getSearchResults(searchTerm) {
      fetch(
        `${routes.predictive_search_url}?q=${searchTerm}&resources[type]=${this.predictiveSearchResources}&resources[limit_scope]=each&resources[limit]=${this.predictiveSearchMaxSize}&section_id=predictive-search`
      )
        .then((response) => {
          if (!response.ok) {
            var error = new Error(response.status);
            this.close();
            throw error;
          }
          return response.text();
        })
        .then((text) => {
          const resultsMarkup = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-predictive-search').innerHTML;
          this.predictiveSearchResults.innerHTML = resultsMarkup;

          this.open();
          this.hidePopularTerms();
        })
        .catch((error) => {
          this.close();
          throw error;
        });
    }

    /**
     * open
     * @description Open the predictive search results.
     */
    open() {
      this.predictiveSearchResults.style.display = 'block';
      this.backdrop?.classList.add('visible');
      this.backdrop?.classList.remove('invisible');
      this.closeOnClickOutside();
    }

    /**
     * close
     * @description Close the predictive search results.
     */
    close() {
      this.predictiveSearchResults.style.display = 'none';
      this.backdrop?.classList.add('invisible');
      this.backdrop?.classList.remove('visible');
    }

    hidePopularTerms() {
      if (this.popularTerms.length > 0) {
        for (let i = 0; i < this.popularTerms.length; i++) {
          this.popularTerms[i].closest('[data-popular-search-terms]').style.display = 'none';
        }
      }
    }

    showPopularTerms() {
      if (
        this.popularTerms.length > 0 &&
        (this.querySelector('[data-predicative-search="results"]').childNodes.length === 0 ||
          this.querySelector('.predictive-search-results__inner').childNodes.length === 0)
      ) {
        for (let i = 0; i < this.popularTerms.length; i++) {
          this.popularTerms[i].closest('[data-popular-search-terms]').style.display = 'block';
        }
      }
    }

    /**
     * handleInputState
     * @description Checks if the input has a value and toggles the input reset button.
     */
    handleInputState() {
      const state = this.input.value.length > 0 ? true : false;
      if (state) {
        this.reset.classList.remove(this.options.hiddenClass);
      } else {
        this.reset.classList.add(this.options.hiddenClass);
        this.close();
      }
      if (document.activeElement === this.input) {
        this.open();
      }

      return state;
    }

    closeOnClickOutside() {
      if (this.backdrop) {
        this.backdrop.addEventListener('click', () => {
          this.close();
          this.hidePopularTerms();
        });
      }
    }

    /**
     * clearInput
     * @description Clears the input.
     */
    clearInput() {
      this.input.value = '';
      this.close();
      this.predictiveSearchResults.innerHTML = '';
    }
  }

  window.PredictiveSearch = PredictiveSearch;

  customElements.define('predictive-search', PredictiveSearch);
}
